import { doc, type Firestore, type Transaction } from 'firebase/firestore';
import type { Day, Expense, TripConfig } from '../types';
import { equal, mergeBookingChange, stripUndef } from './dayWrites';
import { expenseBaseCurrency, isValidAmount } from './expenseUtils';
import { sourceFieldForType, type ExpenseSourceType } from './expenseLinks';
import { destinationDates, SHARED_ITEM_FIELDS } from './destinationItems';
import { readDestinationDocs } from './destinationWrites';
import { chargeToLog, costKindForPaymentStatus } from './bookingCostReview';
import { linkedCostChanged, linkedExpenseSourceChanges } from './linkedExpenseSync';
import { claimFullCost } from './fullCostProtocol';
import { isTripAdmin } from './tripRoles';

const moneyFields = ['amount', 'currency', 'targetAmount', 'targetCurrency', 'exchangeRate', 'fxMarkup'] as const;
const shareFields = ['split', 'extraSplitCount'] as const;
const editableFields = [...moneyFields, ...shareFields, 'desc', 'cat', 'catName', 'note', 'paidStatus', 'depositAmount', 'depositCurrency'] as const;
const basis = (expense: Expense, fields: readonly (keyof Expense)[]) => JSON.stringify(fields.map(key => expense[key]));

/** Delete only the record the user confirmed, never a companion's newer changes. */
export async function writeExpenseDelete(transaction: Transaction, db: Firestore, input: {
  tripId: string; previous: Expense;
}): Promise<boolean> {
  const ref = doc(db, 'trips', input.tripId, 'expenses', input.previous.id);
  const snapshot = await transaction.get(ref);
  if (!snapshot.exists()) return false;
  if (snapshot.data().id !== input.previous.id) throw new Error('This expense changed. Reopen it before deleting.');
  // Reuse the same stable structural comparison as other destructive source edits.
  mergeBookingChange([snapshot.data() as Expense], input.previous, null);
  transaction.delete(ref);
  return true;
}

/** A retained draft ID makes an uncertain save retry safe without replacing history. */
export async function writeExpenseCreate(transaction: Transaction, db: Firestore, input: {
  tripId: string; expense: Expense; userId: string;
}): Promise<{ expense: Expense; created: boolean }> {
  const { tripId, expense, userId } = input;
  if (!expense.id || expense.paid_by !== userId || !expense.desc.trim()
    || !isValidAmount(expense.amount) || !isValidAmount(expense.targetAmount)
    || !expense.split.length || new Set(expense.split).size !== expense.split.length
    || (expense.extraSplitCount !== undefined && (!Number.isInteger(expense.extraSplitCount) || expense.extraSplitCount < 0))) {
    throw new Error('Enter a valid amount, description and traveller split.');
  }
  const ref = doc(db, 'trips', tripId, 'expenses', expense.id);
  const snapshot = await transaction.get(ref);
  if (snapshot.exists()) {
    const current = snapshot.data() as Expense;
    if (basis(current, [...editableFields, 'id', 'paid_by', 'date', 'fullDate', 'sourceId', 'sourceType', 'importedItemDate'])
      !== basis(expense, [...editableFields, 'id', 'paid_by', 'date', 'fullDate', 'sourceId', 'sourceType', 'importedItemDate'])) {
      throw new Error('This expense was already saved with different details. Close this form and open the saved expense to review it.');
    }
    return { expense: current, created: false };
  }
  const tripSnapshot = await transaction.get(doc(db, 'trips', tripId));
  if (!tripSnapshot.exists()) throw new Error('This trip is no longer available.');
  const trip = tripSnapshot.data() as TripConfig;
  const active = (trip.travellers || []).filter(t => t.inviteStatus !== 'pending').map(t => t.id);
  if (!active.includes(userId) || expense.split.some(id => !active.includes(id))
    || expense.targetCurrency !== expenseBaseCurrency(trip)) {
    throw new Error('The trip currency or travellers changed. Review the expense before saving.');
  }
  if (expense.paidStatus !== 'Paid in Full') {
    throw new Error('Log only an amount that a traveller has actually paid.');
  }
  if (expense.sourceId || expense.sourceType) {
    const types: ExpenseSourceType[] = ['transport', 'accommodation', 'attraction', 'food', 'nightlife', 'shopping', 'other'];
    if (!expense.sourceId || !types.includes(expense.sourceType as ExpenseSourceType)) {
      throw new Error('Choose a valid linked item, or leave this expense unlinked.');
    }
    // Provider instalments are progress on a full-cost row, not another debt.
    // Check every live source copy so a stale selected day cannot bypass the link.
    const checkLinkedCosts = async (sources: { expenseLogId?: string; expenseLogIds?: string[] }[]) => {
      const ids = [...new Set(sources.flatMap(source => [source.expenseLogId, ...(source.expenseLogIds || [])]).filter(Boolean))];
      for (const id of ids) {
        const linked = await transaction.get(doc(db, 'trips', tripId, 'expenses', id!));
        if (linked.exists() && (linked.data().amountBasis === 'full-cost' || expense.amountBasis === 'full-cost')) {
          throw new Error('This item already has a linked expense. Its full cost cannot be logged twice; update payment progress on the existing expense instead.');
        }
      }
    };
    const tripStay = expense.sourceType === 'accommodation' && !expense.importedItemDate;
    const stayIndex = tripStay ? (trip.accommodations || []).findIndex(stay => stay.id === expense.sourceId) : -1;
    let exists = stayIndex >= 0;
    if (expense.importedItemDate) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(expense.importedItemDate)) throw new Error('The linked item date is invalid.');
      const field = sourceFieldForType(expense.sourceType as ExpenseSourceType);
      const dates = (SHARED_ITEM_FIELDS as readonly string[]).includes(field)
        ? destinationDates(trip, expense.importedItemDate) : [expense.importedItemDate];
      const days = await readDestinationDocs(transaction, db, tripId, dates);
      const selected = days.find(day => day.date === expense.importedItemDate);
      const selectedData = selected?.data as Day | undefined;
      const items = selectedData?.[field] as { id: string; expenseLogIds?: string[] }[] | undefined;
      const source = Array.isArray(items) ? items.find(item => item.id === expense.sourceId) : undefined;
      const legacyStay = expense.sourceType === 'accommodation' && selectedData?.accommodation?.id === expense.sourceId;
      exists = !!source || !!legacyStay;
      if (exists) await checkLinkedCosts(legacyStay ? [selectedData!.accommodation!] : days.flatMap(day => {
        const current = day.data[field];
        return Array.isArray(current) ? current.filter(item => item.id === expense.sourceId) : [];
      }));
      if (exists) for (const day of days) {
        const data = day.data as Day;
        if (legacyStay) {
          if (day !== selected) continue;
          const stay = data.accommodation!;
          transaction.set(day.ref, { accommodation: { ...stay,
            expenseLogIds: [...new Set([...(stay.expenseLogIds || []), expense.id])] } }, { merge: true });
        } else {
          const current = (data[field] as { id: string; expenseLogIds?: string[] }[] | undefined) || [];
          // Shared Shortlist copies can be absent on old days; use the selected item as seed.
          const item = current.find(value => value.id === expense.sourceId) || source!;
          transaction.set(day.ref, { [field]: current.some(value => value.id === expense.sourceId)
            ? current.map(value => value.id === expense.sourceId ? { ...value,
              expenseLogIds: [...new Set([...(value.expenseLogIds || []), expense.id])] } : value)
            : [...current, { ...item, expenseLogIds: [...new Set([...(item.expenseLogIds || []), expense.id])] }] }, { merge: true });
        }
      }
    }
    if (!exists) throw new Error('The linked item changed or was removed. Reopen the expense and choose it again.');
    if (tripStay) {
      const stays = [...(trip.accommodations || [])];
      const stay = stays[stayIndex];
      await checkLinkedCosts([stay]);
      stays[stayIndex] = { ...stay, expenseLogIds: [...new Set([...(stay.expenseLogIds || []), expense.id])] };
      transaction.update(doc(db, 'trips', tripId), { accommodations: stays });
    }
  }
  const saved = stripUndef(claimFullCost(transaction, db, tripId, expense));
  transaction.set(ref, saved);
  return { expense: saved, created: true };
}

/** Merge only the opened form's changes; preserve live money, history and source metadata. */
export async function prepareExpenseEdit(transaction: Transaction, db: Firestore, input: {
  tripId: string; previous: Expense; next: Expense; userId: string;
}): Promise<{ current: Expense; saved: Expense }> {
  const { tripId, previous, next, userId } = input;
  if (previous.id !== next.id) throw new Error('Reopen this expense before editing it.');
  const ref = doc(db, 'trips', tripId, 'expenses', previous.id);
  const snapshot = await transaction.get(ref);
  if (!snapshot.exists()) throw new Error('This expense was removed. Your changes were not saved.');
  const current = snapshot.data() as Expense;
  if (current.amountBasis === 'full-cost' && linkedCostChanged(previous, next)
    && linkedCostChanged(previous, current)) {
    throw new Error('This expense cost or provider payment changed. Reopen it before saving.');
  }
  const moneyChanged = basis(previous, moneyFields) !== basis(next, moneyFields);
  const shareChanged = basis(previous, shareFields) !== basis(next, shareFields);
  if ((moneyChanged || shareChanged) && (basis(current, moneyFields) !== basis(previous, moneyFields)
    || basis(current, shareFields) !== basis(previous, shareFields)
    || JSON.stringify(current.settlements || {}) !== JSON.stringify(previous.settlements || {}))) {
    throw new Error('This expense or its settlements changed. Reopen it to review the latest amounts before saving.');
  }
  const hasHistory = Object.values(current.settlements || {}).some(s => s.settled > 0 || (s.settledOrig ?? 0) > 0
    || (s as typeof s & { full?: boolean }).full || s.pending);
  if (next.currency !== previous.currency && hasHistory) {
    throw new Error('This expense has settlement history. Keep its original currency so those payments retain their value.');
  }
  if (moneyChanged || shareChanged) {
    if (Object.values(current.settlements || {}).some(s => s.pending)) {
      throw new Error('This expense has a pending settlement. Review it in Settle before changing its amount or split.');
    }
    const tripSnapshot = await transaction.get(doc(db, 'trips', tripId));
    if (!tripSnapshot.exists()) throw new Error('This trip is no longer available.');
    const trip = tripSnapshot.data() as TripConfig;
    const active = (trip.travellers || []).filter(t => t.inviteStatus !== 'pending').map(t => t.id);
    if (next.targetCurrency !== expenseBaseCurrency(trip)
      || next.split.some(id => !previous.split.includes(id) && !active.includes(id))) {
      throw new Error('The trip currency or travellers changed. Reopen the expense before saving.');
    }
  }
  const proposed = { ...previous, ...Object.fromEntries(editableFields.map(key => [key, next[key]])) };
  const saved = mergeBookingChange([current], previous, proposed).saved!;
  if (saved.amountBasis === 'full-cost' && !chargeToLog(costKindForPaymentStatus(saved.paidStatus),
    saved.amount, saved.currency, saved.depositAmount, saved.depositCurrency)) {
    throw new Error('Choose the payment status for this agreed cost.');
  }
  if (!saved.desc.trim() || !isValidAmount(saved.amount) || !isValidAmount(saved.targetAmount)
    || !saved.split.length || new Set(saved.split).size !== saved.split.length
    || (saved.extraSplitCount !== undefined && (!Number.isInteger(saved.extraSplitCount) || saved.extraSplitCount < 0))) {
    throw new Error('Enter a valid amount, description and traveller split.');
  }
  if ((moneyChanged || shareChanged) && current.settlements) {
    // Freeze legacy payments in their original units before changing the bill
    // or denominator; otherwise an old full flag silently grows with the share.
    const oldShare = current.amount / (current.split.length + (current.extraSplitCount || 0));
    const oldUnit = current.targetAmount / current.amount;
    if (!isValidAmount(oldShare) || !isValidAmount(oldUnit)) {
      throw new Error('This expense has invalid historical amounts. Review its settlement history before changing its amount or split.');
    }
    saved.settlements = Object.fromEntries(Object.entries(current.settlements).map(([id, settlement]) => [id,
      settlement.settledOrig !== undefined ? settlement : { ...settlement,
        settledOrig: (settlement as typeof settlement & { full?: boolean }).full ? oldShare : settlement.settled / oldUnit,
      },
    ]));
  }
  if (moneyChanged) {
    delete saved.targetAmountVerified;
    delete saved.rateCache;
  }
  saved.updatedAt = new Date().toISOString();
  if (saved.amountBasis === 'full-cost') saved.fullCostRevision = (current.fullCostRevision || 0) + 1;
  saved.updatedBy = userId;
  return { current, saved };
}

export async function writeExpenseEdit(transaction: Transaction, db: Firestore, input: {
  tripId: string; previous: Expense; next: Expense; userId: string;
  legacySource?: Record<string, unknown>;
}): Promise<Expense> {
  const { tripId } = input;
  const { current, saved } = await prepareExpenseEdit(transaction, db, input);
  if (input.legacySource) {
    const trip = await transaction.get(doc(db, 'trips', tripId));
    if (!trip.exists() || (current.paid_by !== input.userId && !isTripAdmin(trip.data() as TripConfig, input.userId))) {
      throw new Error('Only the payer or a Trip Admin can review this legacy cost.');
    }
    if (current.amountBasis || !current.sourceId || current.itineraryDeleted || !equal(current, input.previous)) {
      throw new Error('This legacy expense changed. Reopen it before reviewing its accounting.');
    }
    if (!chargeToLog(costKindForPaymentStatus(saved.paidStatus), saved.amount, saved.currency, saved.depositAmount, saved.depositCurrency)) {
      throw new Error('Confirm the agreed total and provider payment status before converting this legacy expense.');
    }
    saved.amountBasis = 'full-cost';
    saved.legacyCostReview = { amount: current.amount, currency: current.currency, targetAmount: current.targetAmount,
      targetCurrency: current.targetCurrency, reviewedAt: saved.updatedAt!, reviewedBy: input.userId };
  }
  const sourceChanges = await linkedExpenseSourceChanges(transaction, db, tripId, current, saved, input.legacySource);
  if (input.legacySource && !sourceChanges.length) throw new Error('The legacy booking link needs review before conversion.');
  for (const change of sourceChanges) transaction.update(change.ref, change.data);
  const result = input.legacySource ? claimFullCost(transaction, db, tripId, saved) : saved;
  transaction.set(doc(db, 'trips', tripId, 'expenses', saved.id), stripUndef(result));
  return result;
}
